import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import resourcesApi from '../../utils/resourcesApi';
import { performAdminCheck } from '../../utils/adminUtils';
import LoadingScreen from '../../components/LoadingScreen';
import ResourceAdminMenu from '../../components/resources/ResourceAdminMenu';
import ResourceSearchInput from '../../components/resources/ResourceSearchInput';

const ResourceManagePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentFilter, setCurrentFilter] = useState('all'); // all, published, draft
  const [searchTerm, setSearchTerm] = useState('');
  
  // Check if user is admin
  const checkAdminStatus = useCallback(async () => {
    await performAdminCheck(user, setIsAdmin, navigate, '/resources/manage');
  }, [user, navigate]);
  
  // Fetch all resources (including unpublished ones)
  const fetchResources = useCallback(async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    try {
      // Get all resources including drafts since we're in the admin page
      const options = {
        includeUnpublished: true,
        limit: 100 // Get more resources for admin view
      };
      
      // Apply filter if needed
      if (currentFilter === 'published') {
        options.publishedOnly = true;
      } else if (currentFilter === 'draft') {
        options.draftsOnly = true;
      }
      
      const result = await resourcesApi.getResources(options);
      
      const formattedResources = result.data;
      setResources(formattedResources);
      setFilteredResources(formattedResources);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, currentFilter]);
  
  // Fetch categories
  const fetchCategories = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      const categoriesData = await resourcesApi.getCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [isAdmin]);
  
  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);
  
  useEffect(() => {
    if (isAdmin) {
      fetchResources();
      fetchCategories();
    }
  }, [isAdmin, fetchResources, fetchCategories]);
  
  // Filter resources based on search term
  useEffect(() => {
    if (!resources.length) {
      setFilteredResources([]);
      return;
    }
    
    if (!searchTerm) {
      setFilteredResources(resources);
      return;
    }
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    const filtered = resources.filter(resource => 
      resource.title.toLowerCase().includes(lowerSearchTerm) || 
      resource.summary.toLowerCase().includes(lowerSearchTerm) ||
      resource.tags.some(tag => tag.name.toLowerCase().includes(lowerSearchTerm))
    );
    
    setFilteredResources(filtered);
  }, [resources, searchTerm]);
  
  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term);
  };
  
  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Handle publish/unpublish
  const handlePublishToggle = async (resource) => {
    try {
      const newStatus = !resource.is_published;
      
      // Update the resource with new publish status
      await resourcesApi.updateResource(resource.id, {
        ...resource,
        is_published: newStatus
      });
      
      // Update local state
      const updatedResources = resources.map(r => 
        r.id === resource.id ? { ...r, is_published: newStatus } : r
      );
      setResources(updatedResources);
      
      // Also update filtered resources
      setFilteredResources(prevFiltered => prevFiltered.map(r => 
        r.id === resource.id ? { ...r, is_published: newStatus } : r
      ));
    } catch (error) {
      console.error('Error toggling publish status:', error);
      alert('Failed to update publish status. Please try again.');
    }
  };
  
  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this resource? This action cannot be undone.')) {
      return;
    }
    
    try {
      await resourcesApi.deleteResource(id);
      
      // Remove from local state
      setResources(prevResources => prevResources.filter(r => r.id !== id));
      // Also update filtered resources
      setFilteredResources(prevFiltered => prevFiltered.filter(r => r.id !== id));
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('Failed to delete resource. Please try again.');
    }
  };
  
  if (loading) {
    return <LoadingScreen message="Loading management interface..." />;
  }
  
  if (!isAdmin) {
    return <LoadingScreen message="Checking permissions..." />;
  }
  
  // Find category name by ID
  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Unknown Category';
  };
  
  return (
    <div>
      <ResourceAdminMenu />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Resource Management</h1>
            <p className="mt-1 text-gray-500">Manage all your biotech resources and articles.</p>
          </div>
          <div className="mt-4 md:mt-0">
            <Link
              to="/resources/new"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Create New Resource
            </Link>
          </div>
        </div>
        
        {/* Search and Filters */}
        <div className="mb-6">
          <div className="bg-white shadow rounded-md p-4 mb-4">
            <ResourceSearchInput 
              onSearch={handleSearch}
              placeholder="Search resources by title, content, or tags..."
            />
          </div>
          
          <div className="flex space-x-4 border-b border-gray-200">
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                currentFilter === 'all'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setCurrentFilter('all')}
            >
              All Resources
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                currentFilter === 'published'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setCurrentFilter('published')}
            >
              Published
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                currentFilter === 'draft'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setCurrentFilter('draft')}
            >
              Drafts
            </button>
          </div>
        </div>
      
        {/* Resources table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {filteredResources.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {filteredResources.map(resource => (
                <li key={resource.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12 rounded overflow-hidden bg-gray-100">
                          {resource.featured_image_url ? (
                            <img
                              src={resource.featured_image_url}
                              alt={resource.title}
                              className="h-12 w-12 object-cover"
                            />
                          ) : (
                            <div className="h-12 w-12 flex items-center justify-center">
                              <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-primary-600">
                            <Link to={`/resources/${resource.slug}`} className="hover:underline">
                              {resource.title}
                            </Link>
                          </div>
                          <div className="text-sm text-gray-500 mt-1">{resource.summary.substring(0, 120)}...</div>
                          <div className="mt-2 text-xs text-gray-500 flex flex-wrap items-center">
                            <span className="mr-3">Category: {getCategoryName(resource.category_id)}</span>
                            <span className="mr-3">Views: {resource.view_count || 0}</span>
                            <span className="mr-3">Created: {formatDate(resource.created_at)}</span>
                            {resource.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1 md:mt-0">
                                {resource.tags.map(tag => (
                                  <span 
                                    key={tag.id} 
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                  >
                                    {tag.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:space-x-3">
                        <button
                          onClick={() => handlePublishToggle(resource)}
                          className={`mb-2 sm:mb-0 text-xs px-3 py-1 rounded-md ${
                            resource.is_published
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                          }`}
                        >
                          {resource.is_published ? 'Published' : 'Draft'}
                        </button>
                        
                        <Link
                          to={`/resources/edit/${resource.id}`}
                          className="mb-2 sm:mb-0 text-xs px-3 py-1 rounded-md bg-blue-100 text-blue-800 hover:bg-blue-200 text-center"
                        >
                          Edit
                        </Link>
                        
                        <Link
                          to={`/resources/voice/${resource.id}`}
                          className="mb-2 sm:mb-0 text-xs px-3 py-1 rounded-md bg-purple-100 text-purple-800 hover:bg-purple-200 text-center"
                        >
                          Voice
                        </Link>
                        
                        <button
                          onClick={() => handleDelete(resource.id)}
                          className="text-xs px-3 py-1 rounded-md bg-red-100 text-red-800 hover:bg-red-200"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-8 text-center">
              <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900">No resources found</h3>
              <p className="mt-1 text-gray-500">
                {searchTerm 
                  ? "No resources match your search term." 
                  : currentFilter === 'all' 
                    ? "Get started by creating a new resource." 
                    : `No ${currentFilter === 'published' ? 'published' : 'draft'} resources found.`}
              </p>
              <div className="mt-6">
                <Link
                  to="/resources/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Create New Resource
                </Link>
              </div>
            </div>
          )}
        </div>
        
        {/* Categories management button */}
        <div className="mt-8 flex justify-end">
          <Link
            to="/resources/categories"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            Manage Categories
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResourceManagePage; 